/**
 * FolderApiService - GraphQL API for Folder RBAC
 *
 * E035.3: Backend-first folder management with GraphQL API.
 * Handles CRUD operations for folders with permission checking.
 *
 * SOLID:
 * - S: Single Responsibility - Only handles folder API calls
 * - D: Depends on WorkspaceServerService for GraphQL access
 */
import {
  GraphQLService,
  type WorkspaceServerService,
} from '@affine/core/modules/cloud';
import {
  groupToCachedWorkspacePrincipal,
  mateToCachedWorkspacePrincipal,
  upsertCachedWorkspacePrincipals,
} from '@affine/core/modules/permissions/utils/workspace-principal-cache';
import {
  fetchDocAppResourceLifecycleImpacts,
  fetchFolderAppResourceLifecycleImpacts,
} from '@affine/core/modules/streamlain/native-app/app-resource-lifecycle';
import { workspacePermissionCacheOwnerKey } from '@affine/core/modules/workspace/utils/permission-cache-identity';
import type { WorkspaceMembershipKind } from '@affine/graphql';
import {
  addDocToFolderMutation,
  checkDeletedFolderIdsQuery,
  createFolderMutation,
  type FolderRole as GqlFolderRole,
  getFolderGroupRolesQuery,
  getFolderMateRolesQuery,
  getFolderQuery,
  getFoldersQuery,
  getFolderUsersQuery,
  getGroupsQuery,
  getMateGrantTargetsQuery,
  getTrashedFoldersQuery,
  grantFolderGroupRoleMutation,
  grantFolderMateRoleMutation,
  grantFolderRoleMutation,
  moveFolderMutation,
  permanentlyDeleteFolderMutation,
  type Permission,
  PermissionDenialReason,
  PermissionLifecycleOperation,
  type PermissionLifecyclePreflightInput,
  type PermissionLifecyclePreflightResult,
  PermissionPreflightTargetKind,
  preflightPermissionLifecycleQuery,
  removeDocFromFolderMutation,
  restoreFolderMutation,
  revokeFolderGroupRoleMutation,
  revokeFolderMateRoleMutation,
  revokeFolderRoleMutation,
  trashFolderMutation,
  updateFolderMutation,
} from '@affine/graphql';
import { Service } from '@toeverything/infra';
import { Subject } from 'rxjs';

const GROUP_CACHE_VERSION = 1;
const NAVIGATION_FOLDER_DETAILS_CACHE_TTL_MS = 1_500;
const NAVIGATION_FOLDER_DETAILS_CACHE_MAX_ENTRIES = 256;

/**
 * Lightweight folder type for API responses.
 * Does not include nested fields like children, permissions.
 */
export interface FolderData {
  id: string;
  workspaceId: string;
  parentId: string | null;
  name: string;
  visibility: string;
  inheritPermissions: boolean;
  isWorkspaceRoot: boolean;
  createdAt: string;
  updatedAt: string;
  docCount: number;
}

/**
 * Folder permissions for current user.
 */
export interface FolderPermissions {
  Folder_Read: boolean;
  Folder_Update: boolean;
  Folder_Delete: boolean;
  Folder_Trash: boolean;
  Folder_Restore: boolean;
  Folder_Move: boolean;
  Folder_AddDoc: boolean;
  Folder_RemoveDoc: boolean;
  Folder_CreateSubfolder: boolean;
  Folder_Users_Manage: boolean;
  Folder_Users_Read: boolean;
}

/**
 * Folder owner information.
 */
export interface FolderOwner {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
}

export interface FolderEffectiveAccessEntry {
  principalType: 'user' | 'group';
  principalId: string;
  displayName: string;
  email: string | null;
  avatarUrl: string | null;
  effectiveRole: FolderRole;
  workspaceRole: Permission | null;
  membershipKind: WorkspaceMembershipKind | null;
  sourceType: 'direct' | 'folder' | 'workspace';
  sourceId: string | null;
  sourceName: string | null;
  isDirectOverride: boolean;
  isInherited: boolean;
  isInheritanceBlocked: boolean;
  isAreaBlocked: boolean;
  areaBoundaryKind: string | null;
  areaBoundaryRootFolderId: string | null;
  areaBoundaryRootName: string | null;
  permissionGraphVersion: number;
}

/**
 * Folder with children and permissions.
 */
export interface FolderWithDetails extends FolderData {
  children: FolderData[];
  permissions: FolderPermissions;
  owner: FolderOwner | null;
  effectiveAccess: FolderEffectiveAccessEntry[];
  permissionGraphVersion: number;
}

export type FolderRole = GqlFolderRole;
export type {
  PermissionLifecyclePreflightInput,
  PermissionLifecyclePreflightResult,
};
export {
  PermissionDenialReason,
  PermissionLifecycleOperation,
  PermissionPreflightTargetKind,
};

/**
 * User with folder role assignment.
 */
export interface GrantedFolderUser {
  role: FolderRole;
  workspaceRole: Permission;
  membershipKind: WorkspaceMembershipKind | null;
  sourceType?: 'direct' | 'folder' | 'workspace';
  sourceName?: string | null;
  isDirectOverride?: boolean;
  isInherited?: boolean;
  isInheritanceBlocked?: boolean;
  isAreaBlocked?: boolean;
  areaBoundaryKind?: string | null;
  areaBoundaryRootFolderId?: string | null;
  areaBoundaryRootName?: string | null;
  permissionGraphVersion?: number;
  user: {
    id: string;
    name: string;
    email: string;
    avatarUrl: string | null;
  };
}

/**
 * Paginated folder users response.
 */
export interface PaginatedFolderUsers {
  totalCount: number;
  items: GrantedFolderUser[];
}

/**
 * Group data from workspace.
 */
export interface GroupData {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  isAutoSync?: boolean;
  createdAt?: string;
  memberWorkspaceRoles?: Permission[];
}

interface CachedWorkspaceGroups {
  version: number;
  workspaceId: string;
  groups: GroupData[];
  updatedAt: number;
}

/**
 * Group role assignment on a folder.
 */
/** A workspace Mate by id and name. */
export interface NamedMate {
  id: string;
  name: string;
}

/** The direct role of a workspace Mate on a folder. */
export interface FolderMateRole extends NamedMate {
  role: FolderRole;
}

export interface FolderGroupRole {
  id: string;
  folderId?: string;
  groupId: string;
  role: FolderRole;
  sourceType?: 'direct' | 'folder' | 'workspace';
  sourceName?: string | null;
  isDirectOverride?: boolean;
  isInherited?: boolean;
  isInheritanceBlocked?: boolean;
  isAreaBlocked?: boolean;
  areaBoundaryKind?: string | null;
  areaBoundaryRootFolderId?: string | null;
  areaBoundaryRootName?: string | null;
  permissionGraphVersion?: number;
}

/**
 * Trashed folder data from getTrashedFolders query.
 */
export interface TrashedFolderData {
  id: string;
  workspaceId: string;
  parentId: string | null;
  name: string;
  systemKind: string | null;
  trashed: boolean;
  trashedAt: string | null;
  trashedBy: string | null;
  createdAt: string;
  updatedAt: string;
  docIds: string[];
  permissions?: FolderPermissions;
  owner: FolderOwner | null;
}

export class FolderApiService extends Service {
  /**
   * Emitted after a folder role is successfully granted.
   * FolderDocSyncService subscribes to trigger placement reconciliation
   * (the folder now exists in PG, folder memberships can be synced).
   */
  readonly folderRoleGranted$ = new Subject<{
    workspaceId: string;
    folderId: string;
  }>();

  /**
   * Emitted after a folder trash/restore/permanentDelete operation completes.
   * Trash page subscribes to re-fetch the trashed folders list.
   */
  readonly folderTrashChanged$ = new Subject<void>();

  private readonly navigationFolderDetailsCache = new Map<
    string,
    { value: FolderWithDetails | null; resolvedAt: number }
  >();
  private readonly navigationFolderDetailsPending = new Map<
    string,
    Promise<FolderWithDetails | null>
  >();

  private get graphqlService(): GraphQLService | null {
    return (
      this.workspaceServerService.server?.scope.get(GraphQLService) ?? null
    );
  }

  private get cacheOwnerKey(): string | null {
    const server = this.workspaceServerService.server;
    const accountId = server?.account$?.value?.id;
    if (!server || !accountId) {
      return null;
    }
    return workspacePermissionCacheOwnerKey({
      accountId,
      serverBaseUrl: server.baseUrl,
      serverId: server.id,
    });
  }

  constructor(private readonly workspaceServerService: WorkspaceServerService) {
    super();
  }

  private requireGraphQL(): GraphQLService {
    const gql = this.graphqlService;
    if (!gql) {
      throw new Error(
        'GraphQL service not available - workspace not connected to server'
      );
    }
    return gql;
  }

  private getGroupsCacheStorageKey(workspaceId: string): string | null {
    const cacheOwnerKey = this.cacheOwnerKey;
    if (!cacheOwnerKey || typeof globalThis.localStorage === 'undefined') {
      return null;
    }
    return `streamlain:workspace-groups:${workspaceId}:${cacheOwnerKey}`;
  }

  getCachedGroups(workspaceId: string): GroupData[] {
    const storageKey = this.getGroupsCacheStorageKey(workspaceId);
    if (!storageKey) {
      return [];
    }
    try {
      const raw = globalThis.localStorage.getItem(storageKey);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw) as Partial<CachedWorkspaceGroups>;
      if (
        parsed.version !== GROUP_CACHE_VERSION ||
        parsed.workspaceId !== workspaceId ||
        !Array.isArray(parsed.groups)
      ) {
        return [];
      }
      return parsed.groups.filter(
        group =>
          typeof group.id === 'string' &&
          typeof group.workspaceId === 'string' &&
          typeof group.name === 'string'
      );
    } catch {
      return [];
    }
  }

  private cacheGroups(workspaceId: string, groups: GroupData[]): void {
    const storageKey = this.getGroupsCacheStorageKey(workspaceId);
    const cacheOwnerKey = this.cacheOwnerKey;
    if (!storageKey || !cacheOwnerKey) {
      return;
    }
    try {
      const payload: CachedWorkspaceGroups = {
        version: GROUP_CACHE_VERSION,
        workspaceId,
        groups,
        updatedAt: Date.now(),
      };
      globalThis.localStorage.setItem(storageKey, JSON.stringify(payload));
      upsertCachedWorkspacePrincipals(
        { cacheOwnerKey, workspaceId },
        groups.map(group => groupToCachedWorkspacePrincipal(group))
      );
    } catch {
      // Cache is opportunistic; online GraphQL remains source of truth.
    }
  }

  /**
   * Get the root folder for a workspace.
   * Returns null if no root folder exists (e.g. workspace not yet initialized).
   */
  async getRootFolder(workspaceId: string): Promise<FolderData | null> {
    const topLevel = await this.getFolders(workspaceId, null);
    return topLevel.find(f => f.isWorkspaceRoot) ?? null;
  }

  /**
   * Get all folders for a workspace, optionally filtered by parent.
   */
  async getFolders(
    workspaceId: string,
    parentId: string | null = null
  ): Promise<FolderData[]> {
    const result = await this.requireGraphQL().gql({
      query: getFoldersQuery,
      variables: { workspaceId, parentId },
    });

    return (result.workspace?.folders ?? []).map(this.toFolderData);
  }

  /**
   * Get a single folder with children, permissions, and owner.
   */
  async getFolder(
    workspaceId: string,
    folderId: string
  ): Promise<FolderWithDetails | null> {
    const result = await this.requireGraphQL().gql({
      query: getFolderQuery,
      variables: { workspaceId, folderId },
    });

    // Type assertion for owner field
    const folder = result.workspace?.folder as
      | (typeof result.workspace.folder & {
          owner?: {
            id: string;
            name: string;
            email: string;
            avatarUrl?: string | null;
          };
        })
      | undefined;
    const value = folder
      ? {
          ...this.toFolderData(folder),
          children: folder.children.map(this.toFolderData),
          permissions: folder.permissions,
          effectiveAccess: (folder.effectiveAccess ?? []).map(entry => ({
            principalType: entry.principalType as 'user' | 'group',
            principalId: entry.principalId,
            displayName: entry.displayName,
            email: entry.email ?? null,
            avatarUrl: entry.avatarUrl ?? null,
            effectiveRole: entry.effectiveRole,
            workspaceRole: entry.workspaceRole ?? null,
            membershipKind: entry.membershipKind,
            sourceType: entry.sourceType as 'direct' | 'folder' | 'workspace',
            sourceId: entry.sourceId ?? null,
            sourceName: entry.sourceName ?? null,
            isDirectOverride: entry.isDirectOverride,
            isInherited: entry.isInherited,
            isInheritanceBlocked: entry.isInheritanceBlocked,
            isAreaBlocked: entry.isAreaBlocked,
            areaBoundaryKind: entry.areaBoundaryKind ?? null,
            areaBoundaryRootFolderId: entry.areaBoundaryRootFolderId ?? null,
            areaBoundaryRootName: entry.areaBoundaryRootName ?? null,
            permissionGraphVersion: entry.permissionGraphVersion,
          })),
          permissionGraphVersion: folder.permissionGraphVersion,
          owner: folder.owner
            ? {
                id: folder.owner.id,
                name: folder.owner.name,
                email: folder.owner.email,
                avatarUrl: folder.owner.avatarUrl ?? null,
              }
            : null,
        }
      : null;
    const cacheKey = `${workspaceId}:${folderId}`;
    if (
      !this.navigationFolderDetailsCache.has(cacheKey) &&
      this.navigationFolderDetailsCache.size >=
        NAVIGATION_FOLDER_DETAILS_CACHE_MAX_ENTRIES
    ) {
      const oldestKey = this.navigationFolderDetailsCache.keys().next().value;
      if (oldestKey) {
        this.navigationFolderDetailsCache.delete(oldestKey);
      }
    }
    this.navigationFolderDetailsCache.delete(cacheKey);
    this.navigationFolderDetailsCache.set(cacheKey, {
      value,
      resolvedAt: Date.now(),
    });
    return value;
  }

  /**
   * Navigation rows can remount repeatedly while a tree is reparented or
   * expanded. Share one bounded, short-lived detail read across those mounts.
   * Permission invalidations and settings flows must keep using getFolder().
   */
  getFolderForNavigation(
    workspaceId: string,
    folderId: string
  ): Promise<FolderWithDetails | null> {
    const cacheKey = `${workspaceId}:${folderId}`;
    const cached = this.navigationFolderDetailsCache.get(cacheKey);
    if (
      cached &&
      Date.now() - cached.resolvedAt <= NAVIGATION_FOLDER_DETAILS_CACHE_TTL_MS
    ) {
      return Promise.resolve(cached.value);
    }
    const pending = this.navigationFolderDetailsPending.get(cacheKey);
    if (pending) {
      return pending;
    }
    const request = this.getFolder(workspaceId, folderId).finally(() => {
      if (this.navigationFolderDetailsPending.get(cacheKey) === request) {
        this.navigationFolderDetailsPending.delete(cacheKey);
      }
    });
    this.navigationFolderDetailsPending.set(cacheKey, request);
    return request;
  }

  /**
   * Create a new folder.
   */
  async createFolder(
    workspaceId: string,
    name: string,
    parentId: string | null = null,
    options?: {
      id?: string;
      visibility?: string;
      inheritPermissions?: boolean;
    }
  ): Promise<FolderData> {
    const result = await this.requireGraphQL().gql({
      query: createFolderMutation,
      variables: {
        input: {
          workspaceId,
          name,
          parentId,
          ...(options?.id !== undefined && { id: options.id }),
          ...(options?.visibility !== undefined && {
            visibility: options.visibility,
          }),
          ...(options?.inheritPermissions !== undefined && {
            inheritPermissions: options.inheritPermissions,
          }),
        },
      },
    });

    return this.toFolderData(result.createFolder);
  }

  /**
   * Update folder properties (name, visibility, inheritPermissions).
   */
  async updateFolder(
    workspaceId: string,
    folderId: string,
    data: {
      name?: string;
      visibility?: string;
      inheritPermissions?: boolean;
    }
  ): Promise<FolderData> {
    const result = await this.requireGraphQL().gql({
      query: updateFolderMutation,
      variables: {
        input: { workspaceId, folderId, ...data },
      },
    });

    return this.toFolderData(result.updateFolder);
  }

  async renameFolder(
    workspaceId: string,
    folderId: string,
    name: string,
    _parentId: string | null
  ): Promise<FolderData> {
    return await this.updateFolder(workspaceId, folderId, { name });
  }

  /**
   * Soft-delete (trash) a folder and all its contents recursively.
   * Trashed folders can be restored via restoreFolder().
   */
  async trashFolder(
    workspaceId: string,
    folderId: string
  ): Promise<{ id: string; name: string }> {
    const result = await this.requireGraphQL().gql({
      query: trashFolderMutation,
      variables: { workspaceId, folderId },
    });

    const trashed = {
      id: result.trashFolder.id,
      name: result.trashFolder.name,
    };
    this.folderTrashChanged$.next();
    return trashed;
  }

  /**
   * Restore a trashed folder and its recursively trashed docs.
   */
  async restoreFolder(
    workspaceId: string,
    folderId: string
  ): Promise<{ id: string; name: string }> {
    const result = await this.requireGraphQL().gql({
      query: restoreFolderMutation,
      variables: { workspaceId, folderId },
    });

    const restored = {
      id: result.restoreFolder.id,
      name: result.restoreFolder.name,
    };
    this.folderTrashChanged$.next();
    return restored;
  }

  /**
   * Permanently delete a trashed folder and all its contents.
   * Cannot be undone.
   */
  async permanentlyDeleteFolder(
    workspaceId: string,
    folderId: string
  ): Promise<boolean> {
    const result = await this.requireGraphQL().gql({
      query: permanentlyDeleteFolderMutation,
      variables: { workspaceId, folderId },
    });

    this.folderTrashChanged$.next();
    return result.permanentlyDeleteFolder;
  }

  async preflightPermissionLifecycle(
    input: PermissionLifecyclePreflightInput
  ): Promise<PermissionLifecyclePreflightResult> {
    const result = await this.requireGraphQL().gql({
      query: preflightPermissionLifecycleQuery,
      variables: { input },
    });
    return result.preflightPermissionLifecycle;
  }

  /**
   * Get visible trashed folders for a workspace.
   */
  async getTrashedFolders(workspaceId: string): Promise<TrashedFolderData[]> {
    const result = await this.requireGraphQL().gql({
      query: getTrashedFoldersQuery,
      variables: { workspaceId },
    });

    return (result.workspace?.trashedFolders ?? []).map(
      (f: {
        id: string;
        workspaceId: string;
        parentId: string | null;
        name: string;
        systemKind: string | null;
        trashed: boolean;
        trashedAt: string | null;
        trashedBy: string | null;
        createdAt: string;
        updatedAt: string;
        docIds: string[];
        permissions?: FolderPermissions;
        owner?: {
          id: string;
          name: string;
          email: string;
          avatarUrl?: string | null;
        } | null;
      }): TrashedFolderData => ({
        id: f.id,
        workspaceId: f.workspaceId,
        parentId: f.parentId,
        name: f.name,
        systemKind: f.systemKind,
        trashed: f.trashed,
        trashedAt: f.trashedAt,
        trashedBy: f.trashedBy,
        createdAt: f.createdAt,
        updatedAt: f.updatedAt,
        docIds: f.docIds ?? [],
        permissions: f.permissions,
        owner: f.owner
          ? {
              id: f.owner.id,
              name: f.owner.name,
              email: f.owner.email,
              avatarUrl: f.owner.avatarUrl ?? null,
            }
          : null,
      })
    );
  }

  /**
   * Move folder to a new parent (or root if null).
   */
  async moveFolder(
    workspaceId: string,
    folderId: string,
    newParentId: string | null,
    permissionPreflight?: Promise<unknown>
  ): Promise<FolderData> {
    // The backend enforces both policies authoritatively at the mutation
    // boundary. Start that command immediately; these client reads are only
    // advisory UX checks and must not serialize the common successful move.
    const mutation = this.requireGraphQL()
      .gql({
        query: moveFolderMutation,
        variables: { workspaceId, folderId, newParentId },
      })
      .then(
        result => ({ result, error: null }),
        error => ({ result: null, error })
      );
    await Promise.all([
      permissionPreflight,
      this.assertLinkedResourceMoveAllowed(
        fetchFolderAppResourceLifecycleImpacts(
          workspaceId,
          'move',
          [folderId],
          newParentId
        )
      ),
    ]);
    const outcome = await mutation;
    if (outcome.error) {
      throw outcome.error;
    }

    return this.toFolderData(outcome.result!.moveFolder);
  }

  /**
   * Add a document to a folder.
   */
  async addDocToFolder(
    workspaceId: string,
    folderId: string,
    docId: string,
    permissionPreflight?: Promise<unknown>
  ): Promise<boolean> {
    await this.awaitDocMovePreflights(
      workspaceId,
      docId,
      folderId,
      permissionPreflight
    );
    const result = await this.requireGraphQL().gql({
      query: addDocToFolderMutation,
      variables: { workspaceId, folderId, docId },
    });

    return result.addDocToFolder;
  }

  /**
   * Remove a document from its folder.
   */
  async removeDocFromFolder(
    workspaceId: string,
    docId: string,
    permissionPreflight?: Promise<unknown>
  ): Promise<boolean> {
    await this.awaitDocMovePreflights(
      workspaceId,
      docId,
      null,
      permissionPreflight
    );
    const result = await this.requireGraphQL().gql({
      query: removeDocFromFolderMutation,
      variables: { workspaceId, docId },
    });

    return result.removeDocFromFolder;
  }

  private async awaitDocMovePreflights(
    workspaceId: string,
    docId: string,
    targetFolderId: string | null,
    permissionPreflight: Promise<unknown> | undefined
  ): Promise<void> {
    // Both client-side preflights are read-only and independent. Run them in
    // parallel, but do not start the mutation before both completed. The
    // backend still repeats both policies authoritatively at the mutation
    // boundary; this only removes avoidable client-side serialization.
    await Promise.all([
      permissionPreflight,
      this.assertLinkedResourceMoveAllowed(
        fetchDocAppResourceLifecycleImpacts(
          workspaceId,
          'move',
          [docId],
          targetFolderId
        )
      ),
    ]);
  }

  private async assertLinkedResourceMoveAllowed(
    impactsPromise: ReturnType<typeof fetchDocAppResourceLifecycleImpacts>
  ): Promise<void> {
    const impacts = await impactsPromise;
    if (
      !impacts.some(
        impact => impact.impact === 'linked_resource_requires_same_area'
      )
    ) {
      return;
    }
    throw Object.assign(
      new Error('Linked app resources must stay in the same workspace area'),
      { code: 'LINKED_RESOURCE_REQUIRES_SAME_AREA' as const }
    );
  }

  /**
   * Grant a role to a user for a folder.
   */
  async grantFolderRole(
    workspaceId: string,
    folderId: string,
    userId: string,
    role: FolderRole
  ): Promise<boolean> {
    const result = await this.requireGraphQL().gql({
      query: grantFolderRoleMutation,
      variables: {
        input: { workspaceId, folderId, userId, role },
      },
    });

    if (result.grantFolderRole) {
      this.folderRoleGranted$.next({ workspaceId, folderId });
    }

    return result.grantFolderRole;
  }

  /**
   * Revoke a user's role from a folder.
   */
  async revokeFolderRole(
    workspaceId: string,
    folderId: string,
    userId: string
  ): Promise<boolean> {
    const result = await this.requireGraphQL().gql({
      query: revokeFolderRoleMutation,
      variables: { workspaceId, folderId, userId },
    });

    return result.revokeFolderRole;
  }

  /**
   * Get users with roles for a folder.
   */
  async getFolderUsers(
    workspaceId: string,
    folderId: string,
    skip = 0,
    take = 50
  ): Promise<PaginatedFolderUsers> {
    const result = await this.requireGraphQL().gql({
      query: getFolderUsersQuery,
      variables: {
        workspaceId,
        folderId,
        pagination: { offset: skip, first: take },
      },
    });

    // Type assertion for generated query result
    const data = result as {
      workspace?: {
        folder?: {
          grantedUsers?: {
            totalCount: number;
            items: Array<{
              role: FolderRole;
              workspaceRole: Permission;
              membershipKind: WorkspaceMembershipKind | null;
              isAreaBlocked?: boolean | null;
              areaBoundaryKind?: string | null;
              areaBoundaryRootFolderId?: string | null;
              areaBoundaryRootName?: string | null;
              user: {
                id: string;
                name: string;
                email?: string;
                avatarUrl?: string;
              };
            }>;
          };
        };
      };
    };

    const grantedUsers = data.workspace?.folder?.grantedUsers;
    if (!grantedUsers) {
      return { totalCount: 0, items: [] };
    }

    return {
      totalCount: grantedUsers.totalCount,
      items: grantedUsers.items.map(item => ({
        role: item.role,
        workspaceRole: item.workspaceRole,
        membershipKind: item.membershipKind,
        isAreaBlocked: item.isAreaBlocked ?? false,
        areaBoundaryKind: item.areaBoundaryKind ?? null,
        areaBoundaryRootFolderId: item.areaBoundaryRootFolderId ?? null,
        areaBoundaryRootName: item.areaBoundaryRootName ?? null,
        user: {
          id: item.user.id,
          name: item.user.name,
          email: item.user.email ?? '',
          avatarUrl: item.user.avatarUrl ?? null,
        },
      })),
    };
  }

  /**
   * Get all groups in a workspace.
   */
  async getGroups(workspaceId: string): Promise<GroupData[]> {
    const result = await this.requireGraphQL().gql({
      query: getGroupsQuery,
      variables: { workspaceId },
    });

    const groups = (result.groups ?? []).map(
      (g: {
        id: string;
        workspaceId: string;
        name: string;
        description: string | null;
        isAutoSync?: boolean | null;
        createdAt?: string | null;
        memberWorkspaceRoles?: Permission[] | null;
      }) => ({
        id: g.id,
        workspaceId: g.workspaceId,
        name: g.name,
        description: g.description,
        isAutoSync: g.isAutoSync ?? undefined,
        createdAt: g.createdAt ?? undefined,
        memberWorkspaceRoles: g.memberWorkspaceRoles ?? [],
      })
    );
    this.cacheGroups(workspaceId, groups);
    return groups;
  }

  /**
   * The active workspace Mates a member may grant a role to.
   */
  async getMateGrantTargets(workspaceId: string): Promise<NamedMate[]> {
    const result = await this.requireGraphQL().gql({
      query: getMateGrantTargetsQuery,
      variables: { workspaceId },
    });
    const mates = result.mateGrantTargets.map(mate => ({
      id: mate.mateId,
      name: mate.name,
    }));
    const cacheOwnerKey = this.cacheOwnerKey;
    if (cacheOwnerKey) {
      upsertCachedWorkspacePrincipals(
        { cacheOwnerKey, workspaceId },
        mates.map(mate => mateToCachedWorkspacePrincipal(workspaceId, mate))
      );
    }
    return mates;
  }

  /**
   * The workspace Mates with a direct role on a folder.
   */
  async getFolderMateRoles(
    workspaceId: string,
    folderId: string
  ): Promise<FolderMateRole[]> {
    const result = await this.requireGraphQL().gql({
      query: getFolderMateRolesQuery,
      variables: { workspaceId, folderId },
    });
    return result.folderMateRoles.map(grant => ({
      id: grant.mateId,
      name: grant.name,
      role: grant.role,
    }));
  }

  /**
   * Get group role assignments for a folder.
   */
  async getFolderGroupRoles(
    workspaceId: string,
    folderId: string
  ): Promise<FolderGroupRole[]> {
    const result = await this.requireGraphQL().gql({
      query: getFolderGroupRolesQuery,
      variables: { workspaceId, folderId },
    });

    return (result.folderGroupRoles ?? []).map(
      (r: {
        id: string;
        groupId: string;
        role: GqlFolderRole;
        isAreaBlocked?: boolean | null;
        areaBoundaryKind?: string | null;
        areaBoundaryRootFolderId?: string | null;
        areaBoundaryRootName?: string | null;
      }) => ({
        id: r.id,
        folderId,
        groupId: r.groupId,
        role: r.role,
        isAreaBlocked: r.isAreaBlocked ?? false,
        areaBoundaryKind: r.areaBoundaryKind ?? null,
        areaBoundaryRootFolderId: r.areaBoundaryRootFolderId ?? null,
        areaBoundaryRootName: r.areaBoundaryRootName ?? null,
      })
    );
  }

  /**
   * Grant a group role on a folder.
   */
  async grantFolderGroupRole(
    workspaceId: string,
    folderId: string,
    groupId: string,
    role: string
  ): Promise<FolderGroupRole> {
    const result = await this.requireGraphQL().gql({
      query: grantFolderGroupRoleMutation,
      variables: {
        workspaceId,
        folderId,
        groupId,
        role: role as GqlFolderRole,
      },
    });

    const r = result.grantFolderGroupRole;
    return {
      id: r.id,
      folderId: r.folderId,
      groupId: r.groupId,
      role: r.role,
      isAreaBlocked: r.isAreaBlocked ?? false,
      areaBoundaryKind: r.areaBoundaryKind ?? null,
      areaBoundaryRootFolderId: r.areaBoundaryRootFolderId ?? null,
      areaBoundaryRootName: r.areaBoundaryRootName ?? null,
    };
  }

  /**
   * Revoke a group role from a folder.
   */
  async revokeFolderGroupRole(
    workspaceId: string,
    folderId: string,
    groupId: string
  ): Promise<boolean> {
    const result = await this.requireGraphQL().gql({
      query: revokeFolderGroupRoleMutation,
      variables: { workspaceId, folderId, groupId },
    });

    return result.revokeFolderGroupRole;
  }

  /**
   * Grant a workspace Mate a role on a folder.
   */
  async grantFolderMateRole(
    workspaceId: string,
    folderId: string,
    mateId: string,
    role: string
  ): Promise<boolean> {
    const result = await this.requireGraphQL().gql({
      query: grantFolderMateRoleMutation,
      variables: {
        workspaceId,
        folderId,
        mateId,
        role: role as GqlFolderRole,
      },
    });

    return result.grantFolderMateRole;
  }

  /**
   * Revoke a workspace Mate's role from a folder.
   */
  async revokeFolderMateRole(
    workspaceId: string,
    folderId: string,
    mateId: string
  ): Promise<boolean> {
    const result = await this.requireGraphQL().gql({
      query: revokeFolderMateRoleMutation,
      variables: { workspaceId, folderId, mateId },
    });

    return result.revokeFolderMateRole;
  }

  /**
   * sl-j66d: Check which folder IDs have been permanently deleted (tombstone query).
   * Used by FolderDocSyncService reconcile to distinguish ghost folders from deleted ones.
   */
  async checkDeletedFolderIds(
    workspaceId: string,
    folderIds: string[]
  ): Promise<string[]> {
    if (folderIds.length === 0) return [];

    const result = await this.requireGraphQL().gql({
      query: checkDeletedFolderIdsQuery,
      variables: { workspaceId, folderIds },
    });

    return result.workspace?.deletedFolderIds ?? [];
  }

  /**
   * Map GraphQL response to FolderData.
   */
  private readonly toFolderData = (folder: {
    id: string;
    workspaceId: string;
    parentId: string | null;
    name: string;
    visibility: string;
    inheritPermissions: boolean;
    isWorkspaceRoot?: boolean;
    createdAt: string;
    updatedAt: string;
    docCount: number;
  }): FolderData => ({
    id: folder.id,
    workspaceId: folder.workspaceId,
    parentId: folder.parentId,
    name: folder.name,
    visibility: folder.visibility,
    inheritPermissions: folder.inheritPermissions,
    isWorkspaceRoot: folder.isWorkspaceRoot ?? false,
    createdAt: folder.createdAt,
    updatedAt: folder.updatedAt,
    docCount: folder.docCount,
  });
}
